<script setup lang="ts">
import DarkModeSwitcher from "../../components/DarkModeSwitcher";
import LogginButtons from "./components/LogginButtons.vue";
import SeleccionarEmpresa from "./components/SeleccionarEmpresa.vue";
import { getAuth, signOut } from "firebase/auth";
import LoadingIcon from "../../base-components/LoadingIcon";
import { userUserStore } from "../../stores/user";
import { onBeforeMount, ref } from "vue";
import { asignarCustomClaim } from "../../services/cloudFunctions/asignarCustomClaim";
import router from "../../router";
import { invitacionAceptar } from "../../services/cloudFunctions/invitacionAceptar";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import Button from "../../base-components/Button";
import { usuarioConverter, UsuarioEmpresas } from "@fixeduy/restaurantestypes";

// import {datadogRum} from "@datadog/browser-rum";
import * as Sentry from "@sentry/vue";

const ambiente = import.meta.env.VITE_ENV_NOMBRE;
const userStore = userUserStore();
const db = getFirestore();
const realizandoLogin = ref(false);

const handlerClickLogginButtons = () => {
  realizandoLogin.value = true;
};

const handlerLoginExitoso = async () => {
  Sentry.setUser({
    id: userStore.userLoggedIn?.uid ?? "",
    username: userStore.userLoggedIn?.displayName ?? "",
    email: userStore.userLoggedIn?.email ?? ""
  });
  // datadogRum.setUser({
  //   id: userStore.userLoggedIn?.uid ?? "",
  //   username: userStore.userLoggedIn?.displayName ?? "",
  //   email: userStore.userLoggedIn?.email ?? ""
  // });
  await userStore.cargarInvitaciones();
  realizandoLogin.value = false;
};

const handlerSelecEmpresa = (empresa: UsuarioEmpresas) => {
  // assign custom claim
  asignarCustomClaim({
    empresaSolicitada: empresa.id
  }).then(result => {
    const auth = getAuth();
    if (result.exito && auth.currentUser != null) {
      // load token
      auth.currentUser.getIdTokenResult(true).then(async idTokenResult => {
        userStore.setUserToken(idTokenResult);
        await userStore.setEmpresasActual(empresa);
        router.replace("/");
      });
    } else {
      signOut(auth).then(() => {
        userStore.signOut();
        router.push("/login");
      });
    }
  });
};

onBeforeMount(async () => {
  if (userStore.userLoggedIn != null) {
    const invitacionesEmpresas = await userStore.cargarInvitaciones();
    if (invitacionesEmpresas.length > 0) {
      for (let i = 0; i < invitacionesEmpresas.length; i++) {
        const invitacion = invitacionesEmpresas[i];
        await invitacionAceptar({
          idEmpresa: invitacion.empresaId
        });
      }
    }
    getDoc(
      doc(db, `usuarios/${userStore.userLoggedIn.uid}`).withConverter(
        usuarioConverter
      )
    )
      .then(docUser => {
        if (docUser.exists()) {
          const usuario = docUser.data();
          userStore.setEmpresasRegistradas(usuario.empresas);
          userStore.setOpciones(
            usuario.googleApi,
            usuario.googleApiEmpresasHabilitadas
          );
          if (usuario.empresas.length == 1) {
            handlerSelecEmpresa(usuario.empresas[0]);
          }
        }
      })
      .catch(() => {
        userStore.signOut();
        signOut(getAuth());
      });
  }
});

const cancelarLogin = () => {
  realizandoLogin.value = false;
  userStore.signOut();
  signOut(getAuth());
};

const irProduccion = () => {
  window.location.href = "https://acceso.atiendo.cloud";
};
</script>

<template>
  <div
    :class="[
      '-m-3 sm:-mx-8 p-3 sm:px-8 relative min-h-[100dvh] overflow-x-hidden overflow-y-auto bg-primary xl:bg-white dark:bg-darkmode-800 xl:dark:bg-darkmode-600',
      'before:hidden before:xl:block before:content-[\'\'] before:w-[57%] before:-mt-[28%] before:-mb-[16%] before:-ml-[13%] before:absolute before:inset-y-0 before:left-0 before:transform before:rotate-[-4.5deg] before:bg-primary/20 before:rounded-[100%] before:dark:bg-darkmode-400',
      'after:hidden after:xl:block after:content-[\'\'] after:w-[57%] after:-mt-[20%] after:-mb-[13%] after:-ml-[13%] after:absolute after:inset-y-0 after:left-0 after:transform after:rotate-[-4.5deg] after:bg-primary after:rounded-[100%] after:dark:bg-darkmode-700'
    ]"
  >
    <!-- <DarkModeSwitcher /> -->
    <div class="container relative z-10 sm:px-10">
      <div class="block grid-cols-2 gap-4 xl:grid">
        <!-- BEGIN: Login Info -->
        <div class="flex-col hidden min-h-screen xl:flex">
          <div class="my-auto">
            <img alt="Logo Atiendo" class="w-1/2 -mt-16 -intro-x" />
            <!-- src="/logo_nuevo_blanco_x_color.png" -->
            <div
              class="mt-10 text-4xl font-medium leading-tight text-white -intro-x"
            >
              Tu solución para<br />
              Restaurantes
              <template v-if="ambiente != 'Produccion'">
                <p class="mt-5 text-danger p-3 bg-white">Ambiente DEMO</p>
                <Button variant="primary" class="m-2" @click="irProduccion">
                  Ir ambiente de producción
                </Button>
              </template>
            </div>
          </div>
        </div>

        <div class="flex-col h-auto xl:hidden mt-10">
          <div class="my-auto flex justify-center">
            <img alt="Logo Atiedo" class="w-1/2 -intro-x" />
            <!-- src="/logo_nuevo_blanco_x_color.png" -->
          </div>
          <div
            class="mt-5 text-2xl font-medium leading-tight text-white justify-center"
          >
            <p class="text-center">
              Tu solución para<br />
              Restaurantes
            </p>
            <template v-if="ambiente != 'Produccion'">
              <p class="mt-5 text-danger p-3 bg-white">Ambiente DEMO</p>
              <Button
                variant="secondary"
                size="sm"
                class="mb-2 mr-1"
                @click="irProduccion"
              >
                Ir a producción
              </Button>
            </template>
          </div>
        </div>
        <!-- END: Login Info -->
        <!-- BEGIN: Login Form -->
        <div
          class="flex xl:h-screen py-5 xl:h-auto xl:my-10 overflow-y-auto max-h-[80vh]"
        >
          <div
            class="w-full px-5 py-8 mx-auto xl:my-auto bg-white rounded-md shadow-md xl:ml-20 dark:bg-darkmode-600 xl:bg-transparent sm:text-center xl:p-0 xl:shadow-none sm:w-3/4 lg:w-2/4 xl:w-auto"
          >
            <div v-if="userStore.userLoggedIn && !realizandoLogin">
              <SeleccionarEmpresa />
            </div>
            <div v-else>
              <div v-if="userStore.userLoggedIn && realizandoLogin">
                <div
                  class="flex flex-col items-center justify-end col-span-6 sm:col-span-3 xl:col-span-2"
                >
                  <div class="w-8">
                    <LoadingIcon icon="oval" class="w-8 h-8" />
                  </div>
                  <p class="mt-5 text-center">Iniciando sesión...</p>
                  <div class="flex justify-center mt-5">
                    <div class="flex-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        class="mb-2 mr-1"
                        @click="cancelarLogin"
                      >
                        Cancelar inicio de sesión
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div
                :class="{ hidden: userStore.userLoggedIn && realizandoLogin }"
              >
                <LogginButtons
                  @click="handlerClickLogginButtons"
                  @loginexitoso="handlerLoginExitoso"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- END: Login Form -->
      </div>
    </div>
  </div>
</template>
